'use client';

/* eslint-disable max-lines-per-function */
import type { ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import InstagramFillIcon from 'remixicon-react/InstagramFillIcon';
import LinkedinBoxFillIcon from 'remixicon-react/LinkedinBoxFillIcon';
import MapPinLineIcon from 'remixicon-react/MapPinLineIcon';
import PhoneLineIcon from 'remixicon-react/PhoneLineIcon';
import YoutubeFillIcon from 'remixicon-react/YoutubeFillIcon';
import { socialMedia } from '@/common/components/footer/constants';
import { appConfigs } from '@/common/configs/app.config';

const DESCRIPTION =
  'اپلیکیشن فروشگاه‌ساز ویترو با ویژگی‌هایی ساخته شد تا فروشگاه‌های آنلاین راحت‌تر بفروشن و مشتریان‌شون بهتر بخرن. ویترو سعی می‌کنه تجربه فروش آنلاین رو بهبود ببخشه.';

const ADDRESS =
  'تهران، خیابان مطهری، خیابان علی اکبری، کوچه حاج محمدیان، پلاک ۲۴، طبقه ۳، واحد ۵';

const HOURS_PRIMARY = 'شنبه تا چهارشنبه از ساعت ۸ صبح تا ۱۲ شب -';
const HOURS_SECONDARY = 'روزهای تعطیل از ۸ صبح تا ۴ بعد از ظهر';

const PHONE_DISPLAY = '۰۲۱ - ۹۱۰۹۷۵۶۹';
const PHONE_HREF = 'tel:+982191097569';
const COPYRIGHT = 'تمامی حقوق متعلق به شرکت نقش آواتک (سهامی خاص) می‌باشد ®';

const DESKTOP_RESOURCES = [
  { label: 'سوالات متداول', href: '/faq' },
  { label: 'بلاگ', href: appConfigs.witroMagUrl },
  { label: 'ارتباط با ما', href: '/contact' },
  { label: 'قوانین و مقررات', href: '/privacy-policy' },
  { label: 'حریم خصوصی', href: '/terms-of-service' },
] as const;

const MOBILE_RESOURCES = [
  { label: 'سوالات متداول', href: '/faq' },
  { label: 'بلاگ', href: appConfigs.witroMagUrl },
  { label: 'ارتباط با ما', href: '/contact' },
  { label: 'حریم خصوصی', href: '/terms-of-service' },
  { label: 'قوانین و مقررات', href: '/privacy-policy' },
] as const;

const SOCIAL_BY_IMG: Record<string, string> = Object.fromEntries(
  socialMedia.map((item) => [item.img, item.link])
);

const SOCIAL_LINKS = {
  instagram: SOCIAL_BY_IMG['instagram-fill.svg'] ?? 'https://instagram.com/witro.shop',
  linkedin:
    SOCIAL_BY_IMG['linkedin-box-fill.svg'] ??
    'https://www.linkedin.com/company/naqshava',
  youtube:
    SOCIAL_BY_IMG['youtube.svg'] ??
    'https://www.youtube.com/channel/UCyq-kdyPSiNRsgyyOX1yIQw',
  aparat: SOCIAL_BY_IMG['aparat.svg'] ?? 'https://www.aparat.com/Witro.Shop',
  bale: SOCIAL_BY_IMG['bale.svg'] ?? 'https://ble.ir/witro',
};

const TRUST_BADGES = [
  {
    href: 'https://trustseal.enamad.ir/?id=733449&Code=kjc7yp1c5njSHhnfBSIjm4Obp88vzG0L',
    src: '/assets/images/footer/e-namad.svg',
    alt: 'اینماد',
  },
  {
    href: 'https://tehran.irannsr.org/index.php?module=web_directory&wd_id=104366&ctp_id=1086&id=46409',
    src: '/assets/images/footer/rayane.svg',
    alt: 'سازمان نظام صنفی رایانه‌ای',
  },
  {
    href: 'https://satra.ir',
    src: '/assets/images/footer/satra.svg',
    alt: 'سازمان تنظیم مقررات صوت و تصویر فراگیر',
  },
] as const;

interface ShopFooterProps {
  offsetMobileNav?: boolean;
}

function TrustBadges({
  width,
  height,
}: {
  width: number;
  height: number;
}) {
  return (
    <div dir="ltr" className="flex items-center gap-4">
      {TRUST_BADGES.map((badge) => (
        <Link
          key={badge.alt}
          href={badge.href}
          target="_blank"
          referrerPolicy="origin"
          className="flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white"
          style={{ width, height }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={badge.src}
            alt={badge.alt}
            width={width - 16}
            height={height - 16}
            className="max-h-[80%] max-w-[80%] object-contain"
            onError={(event) => {
              event.currentTarget.style.visibility = 'hidden';
            }}
          />
        </Link>
      ))}
    </div>
  );
}

function SocialButton({
  href,
  label,
  size,
  iconSize,
  children,
}: {
  href: string;
  label: string;
  size: number;
  iconSize: number;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="flex items-center justify-center rounded-[10px] bg-white text-black-500"
      style={{ width: size, height: size }}
    >
      <span
        className="flex items-center justify-center"
        style={{ width: iconSize, height: iconSize }}
      >
        {children}
      </span>
    </Link>
  );
}

function SocialRow({
  size,
  includeBale,
  className = '',
}: {
  size: number;
  includeBale: boolean;
  className?: string;
}) {
  return (
    <div dir="ltr" className={`flex items-center ${className}`}>
      <SocialButton
        href={SOCIAL_LINKS.instagram}
        label="اینستاگرام ویترو"
        size={size}
        iconSize={20}
      >
        <InstagramFillIcon size={20} className="size-5" />
      </SocialButton>
      <SocialButton
        href={SOCIAL_LINKS.linkedin}
        label="لینکدین ویترو"
        size={size}
        iconSize={20}
      >
        <LinkedinBoxFillIcon size={20} className="size-5" />
      </SocialButton>
      <SocialButton
        href={SOCIAL_LINKS.youtube}
        label="یوتیوب ویترو"
        size={size}
        iconSize={20}
      >
        <YoutubeFillIcon size={20} className="size-5" />
      </SocialButton>
      <SocialButton
        href={SOCIAL_LINKS.aparat}
        label="آپارات ویترو"
        size={size}
        iconSize={20}
      >
        <Image
          src="/assets/images/footer/aparat.svg"
          alt=""
          width={20}
          height={20}
          className="size-5 object-contain"
        />
      </SocialButton>
      {includeBale && (
        <SocialButton
          href={SOCIAL_LINKS.bale}
          label="بله ویترو"
          size={size}
          iconSize={20}
        >
          <Image
            src="/assets/images/footer/bale.svg"
            alt=""
            width={20}
            height={20}
            className="size-5 object-contain"
          />
        </SocialButton>
      )}
    </div>
  );
}

export default function ShopFooter({
  offsetMobileNav = false,
}: ShopFooterProps) {
  return (
    <footer
      dir="rtl"
      className={`w-full bg-plp-footer text-silver-400 ${offsetMobileNav ? 'pb-16 lg:pb-0' : ''}`}
    >
      <div className="px-4 pb-6 pt-6 md:hidden">
        <div className="mb-4 flex items-center justify-between">
          <Image
            src="/assets/images/footer/witro-white.svg"
            alt="ویترو"
            width={84}
            height={25}
            className="h-[25px] w-[84px] object-contain"
          />
          <SocialRow size={32} includeBale={false} className="gap-2.5" />
        </div>
        <p className="text-sm font-medium leading-5">{DESCRIPTION}</p>

        <div className="mt-4 space-y-4">
          <div>
            <h2 className="text-base font-semibold leading-6">تماس با ما</h2>
            <div className="mt-3 flex items-start gap-2">
              <MapPinLineIcon size={20} className="mt-0.5 shrink-0" />
              <p className="text-xs font-medium leading-5">{ADDRESS}</p>
            </div>
            <Link
              href={PHONE_HREF}
              className="mt-2.5 flex items-center gap-2 text-xs font-medium leading-5"
            >
              <PhoneLineIcon size={20} className="shrink-0" />
              <span dir="ltr">{PHONE_DISPLAY}</span>
            </Link>
          </div>

          <div>
            <h2 className="text-base font-semibold leading-6">روزهای کاری</h2>
            <p className="mt-3 text-xs font-medium leading-5">
              {HOURS_PRIMARY} {HOURS_SECONDARY}
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold leading-6">منابع</h2>
            <nav className="mt-3 flex flex-col gap-2.5">
              {MOBILE_RESOURCES.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="whitespace-nowrap text-sm font-medium leading-5"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="mt-3.5">
          <TrustBadges width={56} height={56} />
        </div>
        <div className="mt-3.5 h-px w-full bg-white/20" />
        <p className="mt-3.5 text-center text-xs font-normal leading-5 text-silver-200">
          {COPYRIGHT}
        </p>
      </div>

      <div className="mx-auto hidden w-full max-w-[1200px] px-4 pb-7 pt-[62px] md:block md:px-0">
        <div className="flex items-start justify-between gap-8">
          <div className="w-[258px] shrink-0">
            <Image
              src="/assets/images/footer/witro-white.svg"
              alt="ویترو"
              width={122}
              height={36}
              className="h-9 w-[122px] object-contain"
            />
            <p className="mt-6 text-base font-medium leading-[26px]">
              {DESCRIPTION}
            </p>
          </div>

          <div className="w-[298px] shrink-0">
            <h2 className="text-xl font-semibold leading-7">تماس با ما</h2>
            <div className="mt-6 flex items-start gap-2">
              <MapPinLineIcon size={24} className="mt-1 shrink-0" />
              <p className="text-base font-medium leading-6">{ADDRESS}</p>
            </div>
            <Link
              href={PHONE_HREF}
              className="mt-4 flex items-center gap-2 text-base font-medium leading-6"
            >
              <PhoneLineIcon size={24} className="shrink-0" />
              <span dir="ltr">{PHONE_DISPLAY}</span>
            </Link>
            <p className="mt-4 text-sm font-medium leading-[26px]">
              {HOURS_PRIMARY}
              <br />
              {HOURS_SECONDARY}
            </p>
          </div>

          <div className="w-max shrink-0">
            <h2 className="text-xl font-semibold leading-7">منابع</h2>
            <nav className="mt-6 flex flex-col gap-4">
              {DESKTOP_RESOURCES.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="whitespace-nowrap text-base font-medium leading-6"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <TrustBadges width={90} height={97} />
        </div>

        <div className="mt-[47px] h-px w-full bg-white/20" />
        <div className="mt-[27px] flex items-center justify-between">
          <p className="text-base font-normal leading-6 text-silver-200">
            {COPYRIGHT}
          </p>
          <SocialRow size={36} includeBale className="gap-5" />
        </div>
      </div>
    </footer>
  );
}
